import { type PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CalendarBlank,
  CaretDown,
  CaretRight,
  SignOut,
  UserCircle,
} from "@phosphor-icons/react";
import { NavLink, useLocation } from "react-router-dom";
import brandMarkUrl from "../../../../xmirror-mark.png";
import { useAuth } from "../app/auth";
import { adminRoutes } from "../app/routes";
import { useApiQuery } from "../lib/query";
import {
  acknowledgedTaskFailures,
  nextAcknowledgedTaskFailures,
  taskFailureReadStorageKey,
  unreadTaskFailures,
} from "../lib/taskBadge";
import type { AlertSummary, Approval, DashboardSummary } from "../lib/types";
import { DataFreshness } from "./Primitives";

export function AppShell({ children }: PropsWithChildren) {
  const { user, logout, hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const polling = Number(import.meta.env.VITE_QUERY_POLL_INTERVAL_MS || 10000);
  const canReadAlerts = hasPermission("alerts:read");
  const alertSummaryQuery = useApiQuery<AlertSummary>(
    ["alerts", "summary"],
    "/api/alerts/summary",
    {
      enabled: canReadAlerts,
      refetchInterval: canReadAlerts ? polling : false,
    },
  );
  const dashboardSummaryQuery = useApiQuery<DashboardSummary>(
    ["dashboard", "summary"],
    "/api/dashboard/summary",
    { refetchInterval: polling },
  );
  const failedTaskCount = dashboardSummaryQuery.data?.tasks.failed || 0;
  const failureReadStorageKey = taskFailureReadStorageKey(
    user?.tenantGuid,
    user?.accountId,
  );
  const [acknowledgedFailureCount, setAcknowledgedFailureCount] = useState(
    () => acknowledgedTaskFailures(failureReadStorageKey),
  );
  const isTaskRoute =
    location.pathname === "/tasks" || location.pathname.startsWith("/tasks/");
  useEffect(() => {
    setAcknowledgedFailureCount(
      acknowledgedTaskFailures(failureReadStorageKey),
    );
  }, [failureReadStorageKey]);
  useEffect(() => {
    if (!failureReadStorageKey || !dashboardSummaryQuery.data) return;
    const nextAcknowledgedCount = nextAcknowledgedTaskFailures(
      failedTaskCount,
      acknowledgedFailureCount,
      isTaskRoute,
    );
    if (nextAcknowledgedCount === acknowledgedFailureCount) return;
    window.localStorage.setItem(
      failureReadStorageKey,
      String(nextAcknowledgedCount),
    );
    setAcknowledgedFailureCount(nextAcknowledgedCount);
  }, [
    acknowledgedFailureCount,
    failedTaskCount,
    failureReadStorageKey,
    isTaskRoute,
    dashboardSummaryQuery.data,
  ]);
  const unreadFailedTaskCount = isTaskRoute
    ? 0
    : unreadTaskFailures(failedTaskCount, acknowledgedFailureCount);
  const canReviewApprovals = hasPermission("approvals:review");
  const pendingApprovalsQuery = useApiQuery<Approval[]>(
    ["approvals", "pending"],
    "/api/approvals?status=pending&limit=50",
    {
      enabled: canReviewApprovals,
      refetchInterval: canReviewApprovals ? polling : false,
    },
  );
  const visibleRoutes = useMemo(
    () =>
      adminRoutes.filter((route) => {
        if (route.tenantAdminOnly) return user?.roleCode === "tenant_admin";
        return route.permission ? hasPermission(route.permission) : true;
      }),
    [hasPermission, user?.roleCode],
  );
  const activeRoute =
    adminRoutes.find((route) => location.pathname.startsWith(route.path)) ||
    adminRoutes[0];
  const routeGroups = useMemo(
    () => ["设备运营", "治理分析", "平台配置"].map((label) => ({
      label,
      routes: visibleRoutes.filter((route) => route.group === label),
    })).filter((group) => group.routes.length),
    [visibleRoutes],
  );

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <img src={brandMarkUrl} alt="XMirror" />
          <strong>XMirror Connect</strong>
        </div>
        <nav aria-label="主导航">
          {routeGroups.map((group) => <section className="nav-group" key={group.label} aria-label={group.label}>
            <strong>{group.label}</strong>
            {group.routes.map((route) => {
              const Icon = route.icon;
              const badge = route.path === "/alerts"
                ? alertSummaryQuery.data?.activeCount || 0
                : route.path === "/tasks"
                  ? unreadFailedTaskCount + (pendingApprovalsQuery.data?.length || 0)
                  : 0;
              const badgeTone = route.path === "/tasks" && !unreadFailedTaskCount ? "blue" : "red";
              return <NavLink key={route.path} to={route.path} className={({ isActive }) => (isActive ? "active" : "")}>
                <Icon size={19} />
                <span>{route.label}</span>
                <span className="nav-tail">
                  {badge > 0 ? <em className={badgeTone} title={route.path === "/tasks" ? "未查看的失败任务与待审批任务" : "活动告警"}>{badge > 99 ? "99+" : badge}</em> : null}
                  <CaretRight size={14} aria-hidden="true" />
                </span>
              </NavLink>;
            })}
          </section>)}
        </nav>
        <div className="account">
          <UserCircle size={33} />
          <span>
            <strong>{user?.username || "管理员"}</strong>
            <small>{user?.tenantGuid || "XMirror 企业"}</small>
          </span>
          <button aria-label="退出登录" onClick={() => void logout()}>
            <SignOut size={18} />
          </button>
        </div>
      </aside>
      <main className="workspace">
        <header className="topbar">
          <div>
            <h1>{activeRoute.label}</h1>
            <span>{activeRoute.description}</span>
          </div>
          <div className="topbar-actions">
            <DataFreshness
              compact
              updatedAt={dashboardSummaryQuery.data?.generatedAt}
              isFetching={dashboardSummaryQuery.isFetching}
              onRefresh={() => void queryClient.refetchQueries({ type: "active" })}
            />
            <i />
            <span className="current-date">
              <CalendarBlank size={15} />
              {new Intl.DateTimeFormat("zh-CN", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              }).format(new Date())}
              <CaretDown size={12} />
            </span>
          </div>
        </header>
        <div className="page-content">{children}</div>
      </main>
    </div>
  );
}
